// Styles for MerchandisingCoBrandCard go here.
import styled, { css } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';

const borderRadius = 16;
const durationStandard = baseVariables.animations?.durationStandard ?? '300ms';

const imageDimensions = {
  regular: {
    base: {
      width: 150,
      height: 118,
      scaleX: 158 / 150,
      scaleY: 124 / 118,
    },
    lg: {
      width: 125,
      height: 98,
      scaleX: 132 / 125,
      scaleY: 104 / 98,
    },
  },
  square: {
    width: 140,
    height: 111,
    scaleX: 148 / 140,
    scaleY: 117 / 111,
  },
};

const spacing = {
  withVariations: {
    gap: { base: 24, md: 24, lg: 16, xl: 24 },
    padding: { base: [28, 32], md: [28, 24], lg: [16, 20], xl: [42, 32] },
  },
  withoutVariations: {
    gap: { base: 32, md: 16, lg: 24, xl: 48 },
    padding: { base: [69, 32], md: [28, 24], lg: [18, 32], xl: [58, 32] },
  },
};

export const StyledMerchandisingCoBrandCard = styled.div<{ $isVariationsAvailable: boolean }>`
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.07);
  border-radius: ${borderRadius}px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  transition: background-color ${durationStandard} ease;
  &.standard {
    background-image: linear-gradient(to bottom, #d7dfe8, #eceef1);
  }
  ${({ $isVariationsAvailable }) => {
    const s = $isVariationsAvailable ? spacing.withVariations : spacing.withoutVariations;
    return css`
      gap: ${toRem(s.gap.base)};
      padding: ${toRem(s.padding.base[0])} ${toRem(s.padding.base[1])};
      @media ${baseVariables.mediaQuery.md} {
        gap: ${toRem(s.gap.md)};
        padding: ${toRem(s.padding.md[0])} ${toRem(s.padding.md[1])};
      }
      @media ${baseVariables.mediaQuery.lg} {
        gap: ${toRem(s.gap.lg)};
        padding: ${toRem(s.padding.lg[0])} ${toRem(s.padding.lg[1])};
      }
      @media ${baseVariables.mediaQuery.xl} {
        gap: ${toRem(s.gap.xl)};
        padding: ${toRem(s.padding.xl[0])} ${toRem(s.padding.xl[1])};
      }
    `;
  }}
  .merchandising-cobrand-card {
    &__image {
      display: flex;
      transition: transform ${durationStandard} ease;
      transform-origin: center center;
      width: ${toRem(imageDimensions.regular.base.width)};
      @media ${baseVariables.mediaQuery.lg} {
        width: ${toRem(imageDimensions.regular.lg.width)};
      }
      @media ${baseVariables.mediaQuery.xl} {
        width: ${toRem(imageDimensions.regular.base.width)};
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      align-self: stretch;
      width: 100%;
      ${({ $isVariationsAvailable }) =>
        $isVariationsAvailable
          ? css`
              gap: ${toRem(4)};
            `
          : css`
              gap: ${toRem(16)};
              @media ${baseVariables.mediaQuery.md} {
                gap: ${toRem(4)};
              }
              @media ${baseVariables.mediaQuery.lg} {
                gap: ${toRem(16)};
              }
            `}
    }
    &__variations {
      width: 100%;
      padding-top: ${toRem(4)};
      &-label {
        font-weight: 700;
      }
      &-value {
        font-weight: 700;
        color: ${baseVariables.color.alert30};
      }
    }
    &__title {
      text-align: center;
    }

    &__title-merch {
      font-family: AldineMarriottRoman, serif;
      text-align: center;
      font-size: 26px;
      font-weight: 400;
      line-height: normal;
      @media ${baseVariables.mediaQuery.md} {
        font-size: 22px;
      }
      @media ${baseVariables.mediaQuery.xl} {
        font-size: 30px;
      }
    }

    &__footer {
      display: flex;
      flex-direction: column;
      gap: 16px;
      align-self: stretch;
      [class*='m-button'] {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      @media ${baseVariables.mediaQuery.lg} {
        gap: 4px;
      }
      @media ${baseVariables.mediaQuery.xl} {
        gap: 16px;
      }
    }
  }

  /* ========== Square-card specific only ========== */
  &.square-card {
    .merchandising-cobrand-card__image {
      width: ${toRem(imageDimensions.square.width)};
      padding-bottom: ${toRem(5)};
    }
    &:hover .merchandising-cobrand-card__image {
      transform: scale(${imageDimensions.square.scaleX}, ${imageDimensions.square.scaleY});
    }
  }

  /* ========== Tall-card specific only: image scaling on hover ========== */
  &.tall-card {
    &:hover .merchandising-cobrand-card__image {
      transform: scale(${imageDimensions.regular.base.scaleX}, ${imageDimensions.regular.base.scaleY});
      @media ${baseVariables.mediaQuery.lg} {
        transform: scale(${imageDimensions.regular.lg.scaleX}, ${imageDimensions.regular.lg.scaleY});
      }
      @media ${baseVariables.mediaQuery.xl} {
        transform: scale(${imageDimensions.regular.base.scaleX}, ${imageDimensions.regular.base.scaleY});
      }
    }
  }
`;
